import assign from 'lodash/assign';
import clone from 'lodash/clone';
import filter from 'lodash/filter';
import forIn from 'lodash/forIn';
import has from 'lodash/has';
import invokeMap from 'lodash/invokeMap';
import isEmpty from 'lodash/isEmpty';
import isFunction from 'lodash/isFunction';
import isPlainObject from 'lodash/isPlainObject';
import merge from 'lodash/merge';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';
import set from 'lodash/set';
import uniq from 'lodash/uniq';
import URLSearchParams from 'core-js/web/url-search-params';
import jQuery from 'jquery';
import { withScope, captureException, setContext } from '@sentry/browser';
import Highcharts from 'highcharts';
import patternFillInit from 'highcharts/modules/pattern-fill';
import { isNaN } from 'lodash/lang';
import makeMoment from '../common/moment/MomentFactory';
import '../vendor/jquery/jquery.multiselect';
import EventLoggingService from '../eventLogging/EventLoggingService';
import EventService from '../common/event/EventService';
import EvaluationEvent from '../vcom/events/EvaluationEvent';
import FrontendEvents from '../eventLogging/const/FrontendEvents';
import PortalEvent from '../vcom/events/PortalEvent';
import AlertService from '../common/alert/AlertService';
import { GraphEndpoint } from '../common/endpoints/modules/GraphEndpoint';
import HttpService from '../common/http/HttpService';
import TooltipService from './TooltipService';
import Base from '../vcom/Base';
import ChartEvent from '../vcom/events/ChartEvent';
import translate from '../common/localization/translate';
import DateRangePickerExtended from './dateRangePickerExtended';
import MinuteValuesToggle from './minuteValuesToggle';
import PeriodSelection from './periodSelection';
import ChartPortletHead from './ChartPortletHead';
import ComponentSelection from './componentSelection';
import LocalizationService from '../common/localization/LocalizationService';
import CosPhiService from './CosPhiService';
import MeasurementType from '../common/measurementType/MeasurementType';
import SolarPowerForecastService from './SolarPowerForecastService';
import { AlarmsEndpoint } from '../common/endpoints/modules/viiu/AlarmsEndpoint';
import VcomProvider from '../VcomProvider';
import VcomSection from '../vcom/const/VcomSection';
import TimeInMilliseconds from '../common/time/TimeInMilliseconds';
import ModalEvent from '../vcom/events/ModalEvent';
import DataDeviceEvents from '../evaluation/const/DataDeviceEvents';

export default (function ($) {
  function isDiagramCreator(chartType, chartKey) {
    const chartTypesWithDiagramCreator = ['bat', 'meteo', 'met', 'status', 'tracker', 'genset', 'ppc', 'user-defined'];
    return chartTypesWithDiagramCreator.includes(chartType)
      && chartKey.indexOf(GraphEndpoint.EVALUATION_CUSTOM_GRAPH_WITH_TABLE) !== -1;
  }

  const fn = {};

  const abbreviationTypeLabels = {
    ad: 'common.SENSORIC_CALCULATION',
    bat: 'common.BATTERY',
    gak: 'common.STRING_COMBINER',
    genset: 'common.GENSET',
    met: 'common.METER',
    meteo: 'common.METEO',
    powermanagement: 'common.POWERMANAGEMENT',
    ppc: 'common.PPC',
    status: 'common.STATUS',
    tracker: 'common.TRACKER',
    wr: 'common.INVERTER',
  };

  const elementScope = {};

  function triggerChartUpdate(updateFunc, scope, param) {
    param = param || {};
    if (isFunction(updateFunc)) {
      if (isCallerScopeExist(scope)) {
        param = $.extend({ scope: fn.callerScope[scope[0].id] }, param);
      }
      elementScope.id = scope[0].id;
      updateFunc(param);
    } else {
      scope.chart('update', param);
    }
  }

  function isCallerScopeExist(scope) {
    return (scope[0].id !== undefined && fn.callerScope !== undefined && fn.callerScope[scope[0].id] !== undefined);
  }

  function isChartInPortlet() {
    return VCOM.section !== 'evaluation' && VCOM.section !== 'analysis';
  }

  function init(options) {
    patternFillInit(Highcharts);
    if (options.scope !== undefined) {
      fn.callerScope = fn.callerScope || {};
      fn.callerScope[options.scope.idUpdateContainer] = options.scope;
      delete options.scope;
    }

    return this.each(function () {
      const $this = $(this);
      let data = $this.data('chart');
      // If the plugin already initialized
      if (data) {
        data.opts.period = options.period;
        return;
      }
      // extend options
      const opts = $.extend({ prevNextControls: false }, options);
      // extend options.chart object
      opts.chart = $.extend({
        url: GraphEndpoint.GRAPH_WITH_TABLE,
        displayType: 'highcharts',
        theme: VcomProvider.Layout.getTheme() === 'mcflat' ? 'light' : 'dark',
      }, options.chart);
      // init data obj
      data = {
        target: $this,
        opts,
      };

      if (data.opts.datepicker) {
        data.opts.pickerExtended = new DateRangePickerExtended($, makeMoment);
        data.opts.pickerExtended.init(data.opts.datepicker);
        data.opts.pickerExtended.setRangePickMode(data.opts.period === 'custom');
        data.opts.pickerExtended.bindListeners($this, data.opts);
      }

      if (data.opts.prevNextControls) {
        bindPrevNextControlsListeners($this, data.opts);
      }

      if (data.opts.periodControl) {
        data.opts.periodSelection = new PeriodSelection();
        data.opts.periodSelection.init(data.opts.periodControl, data.opts.period);
        data.opts.periodSelection.bindListeners($this, data.opts);
      }

      if (data.opts.minuteValuesControl) {
        data.opts.minuteValuesToggle = new MinuteValuesToggle(VCOM, $);
        data.opts.minuteValuesToggle.init(
          data.opts.minuteValuesControl,
          options.chart.systemId,
          options.chart.type,
        );
        data.opts.minuteValuesToggle.bindListeners($this, data.opts);
      }

      bindChartSelectListeners(data, $this);
      bindEvaluationResize(data);
      bindListeners(data, $this);
      $this.data('chart', data);

      if (data.opts.caller === 'chart-creator') {
        const { type, key } = data.opts.chart;
        EventService.dispatch(EvaluationEvent.CHART_SELECTED, {
          data: { chartKey: key, chartType: type },
        });
      }
    });
  }

  function getDataDeviceTypes() {
    return ['met', 'bat', 'meteo', 'tracker', 'pcu', 'status', 'genset', 'ppc'];
  }

  function bindEvaluationResize(chart) {
    jQuery(window).on('resize', function () {
      const mcChartContent = jQuery('#chartWidget .mc-chart-content');
      const isChartDefined = mcChartContent.length > 0 && jQuery('#chartWidget').find('.chart-canvas').highcharts() !== undefined;
      if (isChartNotHeatmap(chart) && isChartDefined) {
        jQuery('#chartWidget').find('.chart-canvas').highcharts().setSize(mcChartContent.width() - 20, mcChartContent.height() * 0.75);
      }
    });
  }

  function isChartNotHeatmap(chart) {
    return chart.chartObj?.options?.chart?.type !== 'heatmap';
  }

  function callObjectMethod(obj, method) {
    if (obj !== undefined && isFunction(obj[method])) {
      try {
        return obj[method]();
      } catch (e) {
      }
    }
  }

  function bindListeners(data, scope) {
    $('.updateChart', scope).on('click', function () {
      if (VCOM.analysis) {
        VCOM.analysis.unhighlightUpdateButton();
      }
      for (let i = 0; i < 2; i += 1) {
        const elChartElementSelection = $('.chart-element-selection-' + i);
        const selectedElements = elChartElementSelection.find(':selected');
        const elementSelectionButtons = elChartElementSelection.next('button');
        const visibleElementSelectionButtons = elementSelectionButtons.find(':visible');
        const elementSelectionIsRequired = visibleElementSelectionButtons.length > 0 && selectedElements.length < 1;

        $(elementSelectionButtons)
          .toggleClass('ui-state-default', !elementSelectionIsRequired)
          .toggleClass('ui-state-error', elementSelectionIsRequired);
      }

      if ($('#messwertSelection button.ui-state-error').length > 0) {
        AlertService.showError(translate('javascript.TX_NO_ELEMENTS_ASSIGNED'));
        return false;
      }

      data.opts.period = data.opts.periodSelection.getCurrentPeriod();

      if (data.opts.periodControl) {
        data.opts.periodSelection.resetPeriods([], data.opts);
      }

      let param = { period: data.opts.period };
      param = data.opts.pickerExtended.extendParamsByPickersStartAndEndDate(param);

      triggerChartUpdate(data.opts.updateFunc, scope, param);
      if ($('#chartComponentSelection').css('display') === 'block') {
        slideUpComponentSelection();
      }
    });

    $('.excelExport', scope).on('click', function () {
      triggerChartUpdate(data.opts.updateFunc, scope, { displayType: 'excel' });
    });

    $('.todayButton', scope).on('click', function () {
      data.opts.pickerExtended.setToday();
      triggerChartUpdate(data.opts.updateFunc, scope);
    });

    $(document, scope).on('click', '.save-diagram', async function () {
      EventService.dispatch(ModalEvent.SAVE_CUSTOM_DIAGRAM);
    });

    EventService.addListener(ChartEvent.DATE_SELECTED_ON_SCHEMA_TABLE, {
      callback: (event) => {
        const { date } = event.detail;
        data.opts.pickerExtended.modifyStartAndEnd(data.opts.period, date);
        triggerChartUpdate(data.opts.updateFunc, scope);
      },
    });

    EventService.addListener(PortalEvent.CURRENT_DAY_PASSED, {
      callback: () => {
        const chart = $(`#${scope.attr('id')}`).data('chart');

        if (chart === undefined) {
          return;
        }

        const datePicker = chart.opts.pickerExtended.getDatepicker();
        datePicker.maxDate = makeMoment();

        if (wasLatestDateSelected(chart.opts.period, makeMoment(), datePicker.startDate) === true) {
          data.opts.pickerExtended.setToday();
          triggerChartUpdate(data.opts.updateFunc, scope);
        }
      },
    });
  }

  function wasLatestDateSelected(period, currentDateMoment, chartDateMoment) {
    const currentDate = currentDateMoment.clone();
    const chartDate = chartDateMoment.clone();

    const datepickerUnitMap = {
      tag: 'day',
      monat: 'month',
      jahr: 'year',
    };

    currentDate.startOf('day');

    switch (period) {
      case 'tag':
      case 'monat':
      case 'jahr': {
        chartDate.startOf(datepickerUnitMap[period]);
        const timePeriodDifference = currentDate.diff(
          chartDate.format(),
          datepickerUnitMap[period],
          true,
        );

        return timePeriodDifference <= 1;
      }
      case 'woche': {
        chartDate.startOf('day');
        const timePeriodDifference = currentDate.diff(chartDate.format(), 'day');

        return timePeriodDifference <= 7;
      }
      default: {
        return false;
      }
    }
  }

  function getAvailablePeriodsForChartType(chartType) {
    switch (chartType) {
      case 'status':
        return ['tag', 'woche'];

      case 'ppc':
        return ['tag'];

      default:
        return ['tag', 'woche', 'monat', 'jahr', 'gesamt'];
    }
  }

  async function onEvaluationChartClicked(data, scope, chartKey, chartType, customChartId, period, displayMinuteValues) {
    scope.chart('actualizeZusatzinfo', null);
    $('.chart-canvas', scope).empty();

    const isSolarPowerForecast = chartKey === 'SPF01';
    const isInverterHeatmap = chartKey === 'Q6ZWP';
    const isWrOrGak = (chartType === 'Wechselrichter' || chartType === 'Gak' || chartType === 'mppt') || isInverterHeatmap;
    const isSatelliteData = chartKey === 'OL84K';
    const isNewComponentSelection = (chartType === 'meteo' || chartType === 'met' || chartType === 'bat' || chartType === 'tracker')
      && !isDiagramCreator(chartType, chartKey)
      && !isSatelliteData;
    if (isInverterHeatmap) {
      chartType = 'Wechselrichter';
    }

    if (period) {
      data.opts.periodSelection.highlightPeriod(period);
      data.opts.chart.period = period;
    }
    data.opts.chart.key = chartKey;
    data.opts.chart.chartType = chartType;
    data.opts.chart.url = GraphEndpoint.GRAPH_WITH_TABLE;
    if (isDiagramCreator(chartType, chartKey)) {
      data.opts.chart.url = GraphEndpoint.EVALUATION_CUSTOM_GRAPH_WITH_TABLE;
      setChartInfo(
        translate('evaluation.DIAGRAM_GENERATOR'),
        translate('evaluation.DIAGRAM_GENERATOR_DESCRIPTION'),
        data,
      );
      if (!isChartInPortlet()) {
        logEvaluationDiagramCreatorClicked(chartType);
      }
    }

    if (chartType === 'user') {
      const periods = getAvailablePeriodsForChartType(chartType);
      data.opts.periodSelection.resetPeriods(periods, data.opts);
      data.opts.chart.url = GraphEndpoint.EVALUATION_USER_GRAPH_WITH_TABLE;
      data.opts.chart.customChartId = customChartId;
      data.opts.chart.isCustomChart = true;
      setChartInfo(
        translate('evaluation.DIAGRAM_GENERATOR'),
        translate('evaluation.DIAGRAM_GENERATOR_DESCRIPTION'),
        data,
      );
      if (!isChartInPortlet()) {
        logEvaluationDiagramCreatorClicked(chartType);
      }
    }

    if (data.opts.staticCharts && data.opts.periodControl) {
      if (chartType === 'status' || isSolarPowerForecast) {
        data.opts.periodSelection.disableAllPeriods();
      } else {
        const chartPeriods = getAvailablePeriodsForChart(chartKey, data.opts.staticCharts);

        const chartDoesNotSupportSelectedPeriod = !chartPeriods.includes(data.opts.periodSelection.getCurrentPeriod());

        data.opts.period = data.opts.periodSelection.getCurrentPeriod();
        data.opts.periodSelection.resetPeriods(chartPeriods, data.opts);
        if (chartDoesNotSupportSelectedPeriod) {
          data.opts.pickerExtended.modifyStartAndEnd(data.opts.periodSelection.getCurrentPeriod());
        }
      }

      if (data.opts.minuteValuesToggle) {
        data.opts.minuteValuesToggle.reset(chartType, chartKey, data.opts.periodSelection.getCurrentPeriod());
        if (chartType !== 'user' || displayMinuteValues) {
          data.opts.minuteValuesToggle.getMinuteValuesToggle(data.opts.chart.systemId, chartType);
        }
      }
    }

    if (isWrOrGak) {
      await drawComponentSelection(data.opts.chart.systemId, chartType, chartKey, scope);
      jQuery('#dataDeviceSelection').addClass('d-none');
    } else if (isNewComponentSelection) {
      EventService.dispatch(DataDeviceEvents.DATA_DEVICES_DESELECT_ALL);
      fn.resetComponentSelection();
      jQuery('#dataDeviceSelection').removeClass('d-none');
    } else {
      jQuery('#conComponentSelection').slideUp();
      fn.resetComponentSelection();
      jQuery('#dataDeviceSelection').addClass('d-none');
    }

    if (chartKey && chartKey.indexOf('custom') !== -1) {
      scope.chart('initMesswertauswahl', chartType);
      const periods = getAvailablePeriodsForChartType(chartType);
      data.opts.periodSelection.resetPeriods(periods, data.opts);
      jQuery('#conMesswertSelection').slideDown('fast');
    } else {
      jQuery('#conMesswertSelection').slideUp('fast');
    }

    if (isSolarPowerForecast) {
      SolarPowerForecastService.applyRestrictions(data.opts.pickerExtended, data.opts.periodSelection);
    } else {
      SolarPowerForecastService.removeRestrictions(data.opts.pickerExtended);
    }

    scope.chart('update');
  }

  function bindChartSelectListeners(data, scope) {
    jQuery('.ffChartSelect').on('change', async function () {
      const chartKey = jQuery(this).val();
      scope.chart('actualizeZusatzinfo', null);
      $('.chart-canvas', scope).empty();

      if (chartKey === '0' || chartKey === null) {
        showNoSelectionPlaceholder(scope);
        return false;
      }
      data.opts.period = jQuery(this).find('option:selected').data('period');
      data.opts.chart.chartId = chartKey;
      if (data.opts.periodControl) {
        data.opts.periodSelection.resetPeriods([], data.opts);
      }
      data.opts.pickerExtended.setToday();
      let param = {
        chartId: chartKey,
        period: data.opts.period,
      };
      param = data.opts.pickerExtended.extendParamsByPickersStartAndEndDate(param);

      triggerChartUpdate(data.opts.updateFunc, scope, param);
      return false;
    });

    if (data.opts?.type === 'evaluation') {
      EventService.addListener(
        EvaluationEvent.CHART_SELECTED,
        {
          callback: (event) => {
            const {
              chartKey,
              chartType,
              customChartId,
              period,
              displayMinuteValues,
            } = event.detail;
            onEvaluationChartClicked(data, scope, chartKey, chartType, customChartId, period, displayMinuteValues);
          },
        },
      );
    }
  }

  fn.multiSelect = {
    initAxisData: ($multi, leftOrRightIndex) => {
      $multi.data('axis_' + leftOrRightIndex, { unit: false, values: [] });
    },
    createMainSelect: function createMainSelect(messdaten, indexLeftRight, scope) {
      const confMain = $('<select>', {
        multiple: 'multiple',
        width: 'auto',
      }).addClass('chart-main-values');
      // add index for left/right side to data
      confMain.data('i', indexLeftRight);

      jQuery.each(messdaten, function (abbreviationType) {
        const label = translate(abbreviationTypeLabels[abbreviationType]);
        const optgroup = $('<optgroup>', { label });
        jQuery.each(this, function (abbreviation) {
          if (this.sBezeichnung === '') {
            return;
          }
          // Gefählrich: Doppelte ID mit dem generierten Multiselect (->init mutiselectPlugin)
          // Zugriff in Zeile unit = $('#'+$(checkbox).attr('id')).attr('rel')
          const option = $('<option>', {
            id: [scope.attr('id'), 'ffMain', indexLeftRight, abbreviation].join('_'),
            value: abbreviation,
            text: this.sBezeichnung,
            rel: this.sEinheit,
          });
          optgroup.append(option);
        });
        confMain.append(optgroup);
      });
      return confMain;
    },
    createWrSelect: function createWrSelect(inverters, indexLeftRight, scope) {
      const confWrAuswahl = $('<select>', {
        multiple: 'multiple',
      }).addClass('chart-element-selection-' + indexLeftRight + ' chart-wr-selection-' + indexLeftRight);

      jQuery.each(inverters, function (key) {
        const option = $('<option>', {
          id: [scope.attr('id'), 'ffWr', indexLeftRight, key].join('_'),
          value: key,
          text: this.name,
          type: 'checkbox',
          name: ['wr', indexLeftRight, '[]'].join(),
        });

        confWrAuswahl.append(option);
      });
      return confWrAuswahl;
    },
    createGakSelect: function createGakSelect(wechselrichter, indexLeftRight, scope) {
      const confGakAuswahl = $('<select>', {
        multiple: 'multiple',
      }).addClass('chart-element-selection-' + indexLeftRight + ' chart-gak-selection-' + indexLeftRight);

      jQuery.each(wechselrichter, function (key) {
        const option = $('<option>', {
          id: [scope.attr('id'), 'ffWr', indexLeftRight, key].join('_'),
          value: key,
          text: this.name,
        });
        confGakAuswahl.append(option);
      });
      return confGakAuswahl;
    },
    createDatadeviceSelect: function createDatadeviceSelect(datadevices, indexLeftRight, scope) {
      const confDataDeviceAuswahl = $('<select>', {
        multiple: 'multiple',
      }).addClass('chart-element-selection-' + indexLeftRight + ' chart-ddv-selection-' + indexLeftRight);

      $.each(datadevices, function (key) {
        const option = $('<option>', {
          id: [scope.attr('id'), 'ffDdv', indexLeftRight, key].join('_'),
          value: key,
          text: this.name,
        });
        confDataDeviceAuswahl.append(option);
      });
      return confDataDeviceAuswahl;
    },
    getMainSelectConfig: function getMainSelectConfig(scope) {
      const config = {
        height: 450,
        minWidth: 415,
        headerUncheckOnly: true,
        selectedList: 4,
        checkAllText: translate('javascript.TX_SELECTALL'),
        uncheckAllText: translate('javascript.TX_UNCHECKALL'),
        noneSelectedText: translate('javascript.TX_SELECTION'),
        allowOptgroupSelect: false,
      };

      config.optgroupToggle = () => false;

      config.checkAll = function () {
        const $multi = $(this);
        const leftOrRightIndex = $multi.data('i');
        const data = scope.data('chart');
        const { chartType } = data.opts.chart;
        const dataDeviceDiagramCreators = getDataDeviceTypes();

        if (dataDeviceDiagramCreators.includes(chartType)) {
          $('select.chart-ddv-selection-' + leftOrRightIndex).next('button').show();
        }
      };

      config.uncheckAll = function () {
        const $multi = $(this);
        const inputFields = $multi.multiselect('widget').find(':checkbox');
        const i = $multi.data('i');

        scope.data('axis_' + i, {
          unit: false,
          values: [],
        });

        $('select.chart-wr-selection-' + i).next('button').hide();
        $('select.chart-gak-selection-' + i).next('button').hide();
        $('select.chart-ddv-selection-' + i).next('button').hide();

        $.each(inputFields, function () {
          $(this).parent().parent().show();
        });
      };

      config.close = function () {
        const mainValues = $(this).val() || [];
        const types = fn.multiSelect.getMainValueTypes(mainValues);
        const i = $(this).data('i');
        const data = scope.data('chart');

        const currentDatadeviceSelection = $('.chart-ddv-selection-' + i);
        const oldSelectedDataDevices = currentDatadeviceSelection.val();
        currentDatadeviceSelection.empty();

        $.each(types, function (key, type) {
          if (getDataDeviceTypes().includes(type)) {
            const datadevices = pickBy(
              data.opts.datadevices,
              function (datadevice) {
                return datadevice.type.toUpperCase() === type.toUpperCase();
              },
            );
            const newDatadeviceSelection = fn.multiSelect.createDatadeviceSelect(datadevices, i, scope);
            currentDatadeviceSelection.append(newDatadeviceSelection.find('option'));
          }
        });

        if (currentDatadeviceSelection.find('option').length > 0) {
          currentDatadeviceSelection.val(oldSelectedDataDevices);
        }

        currentDatadeviceSelection.multiselect('refresh');
      };

      config.click = function (event, data) {
        const $multi = $(this);
        const checkbox = event.originalEvent.currentTarget;
        const simulatedGM0 = $("input[value='sim.G_M0']");
        if ($(checkbox).val() === 'sim.G_M0') {
          const disabled = !simulatedGM0[0].checked && !simulatedGM0[1].checked;
          $('#chartWidget').find('.excelExport').prop('disabled', !disabled);
          if (!disabled) {
            AlertService.showWarning(translate('javascript.LBL_1002'));
          }
        }
        const inputFields = $multi.multiselect('widget').find(':checkbox');
        // The rel attribute is not set on the input field but instead on the option tag!
        let optionId = $(checkbox).attr('id');
        let unit = $('#' + optionId).attr('rel');
        const i = $multi.data('i');

        // on uncheck -> check for any checked values and set unit accordingly
        if (!data.checked) {
          unit = false;
          $.each(inputFields, function () {
            if (this.checked) {
              optionId = $(checkbox).attr('id');
              unit = $('#' + optionId).attr('rel');
              return false;
            }
          });
        }

        if (scope.data('axis_' + i) === undefined) {
          fn.multiSelect.initAxisData(scope, i);
        }

        // uncheck and hide all main-configuration values with different unit
        $.each(inputFields, function () {
          const $this = $(this);
          optionId = $this.attr('id');
          if (unit !== false && unit !== $('#' + optionId).attr('rel')) {
            $this.prop('checked', false);
            $this.parent().parent().hide();
          } else {
            $this.parent().parent().show();
          }
        });

        // update data-field with current unit
        // set new messwertauswahl on data object of chart
        scope.data('axis_' + i).values = [];
        scope.data('axis_' + i).unit = unit;
        scope.data('axis_' + i).subsystems = [];
        scope.data('axis_' + i).inverters = []; // neu fuer gaks
        scope.data('axis_' + i).datadevices = [];

        // check for existing wr-values and show/hide teilanlagen-selection
        // and check for existing gak-values and show/hide wechselrichter-selection
        $('select.chart-wr-selection-' + i).next('button').hide();
        $('select.chart-gak-selection-' + i).next('button').hide();
        $('select.chart-ddv-selection-' + i).next('button').hide();
        $.each(inputFields, function () {
          if (!this.checked) {
            return;
          }
          const abbrSplitted = checkbox.value.split('.');

          let abbreviationType;
          if (abbrSplitted.length === 1) {
            abbreviationType = 'ad';
          } else {
            [abbreviationType] = abbrSplitted;
          }
          if (abbreviationType === 'wr') {
            $('select.chart-wr-selection-' + i).next('button').show();
          }
          if (abbreviationType === 'gak') {
            $('select.chart-gak-selection-' + i).next('button').show();
          }
          if (jQuery.inArray(abbreviationType, getDataDeviceTypes()) !== -1) {
            $('select.chart-ddv-selection-' + i).next('button').show();
          }
          return false;
        });
      };

      return config;
    },
    getMainValueTypes: function getMainValueTypes(mainValues) {
      return uniq($.map(mainValues, function (mainValue) {
        const valueSplitted = mainValue.split('.');
        return valueSplitted.length === 1 ? 'ad' : valueSplitted[0];
      }));
    },
  };
  function initMesswertauswahl(group) {
    const $this = $(this);
    const data = $this.data('chart');

    $('#messwertSelection').empty();

    const definedGroups = ['ad', 'meteo', 'wr', 'met', 'gak', 'bat', 'tracker', 'genset', 'ppc'];
    const measurementList = group === 'user-defined'
      ? pick(data.opts.messdaten, definedGroups)
      : pick(data.opts.messdaten, group);

    // selection for Diagrammgenerator
    // selection: right side = 1; left side = 0
    const selection = {};
    for (let i = 0; i < 2; i += 1) {
      const headline = (i === 0) ? 'javascript.TX_LEFT_AXIS' : 'javascript.TX_RIGHT_AXIS';
      // Selection of main values (shown on top)
      const confMain = fn.multiSelect.createMainSelect(measurementList, i, $this);
      // Selection of SubValues (shown under selection of main values)
      const confWrAuswahl = fn.multiSelect.createWrSelect(data.opts.inverters, i, $this);
      // new for GAK
      const confGakAuswahl = fn.multiSelect.createGakSelect(data.opts.inverters, i, $this);

      const selectionWrapper = '<div class="mc-chart-config"><h3>' + translate(headline) + '</h3></div>';
      selection[i] = $('<td style="width:35%; vertical-align:top;" class="mc-chart-config-' + i + '"></td>');
      selection[i].append($(selectionWrapper).append(confMain))
        .append('<br/>')
        .append(confWrAuswahl)
        .append(confGakAuswahl);

      $('.mc-chart-config-' + i + ' .chart-main-values').data('i', i);

      confWrAuswahl.multiselect({
        noneSelectedText: translate('javascript.TX_ELEMENTSELECTION'),
        selectedList: 4,
      });
      confWrAuswahl.next().hide();

      confGakAuswahl.multiselect({
        noneSelectedText: translate('javascript.TX_ELEMENTSELECTION'),
        selectedList: 4,
      });
      confGakAuswahl.next().hide();

      if (data.opts.datadevices !== undefined) {
        const confDataDeviceAuswahl = fn.multiSelect.createDatadeviceSelect(data.opts.datadevices, i, $this);
        selection[i].append(confDataDeviceAuswahl);
        confDataDeviceAuswahl.multiselect({
          noneSelectedText: translate('javascript.TX_ELEMENTSELECTION'),
          selectedList: 4,
        });
        confDataDeviceAuswahl.next().hide();
      }
    }

    $('#messwertSelection')
      .append(selection[0])
      .append('<td style="width:30%"></td>')
      .append(selection[1]);

    // init mutiselectPlugin
    for (let i = 0; i < 2; i += 1) {
      // init main multiselect
      $('.mc-chart-config-' + i + ' .chart-main-values').multiselect(fn.multiSelect.getMainSelectConfig($this));
    }
  }

  fn.onUpdateSuccess = function (response, calledScope, callback, data, params, $this) {
    $('div.chart-error', $this).hide();
    if (response.displayType === 'excel' || response.displayType === 'excelEvent') {
      handleResponseExcel(data, response, $this);
    } else {
      if (response.error) {
        handleResponseError(data, response, $this);
      } else if (response.displayType === 'highcharts') {
        fn.handleResponseDisplayTypeHighcharts(data, response, $this, params);
      } else if (params.dataSource === 'MD-API') {
        handleResponseMdApi(response, params, $this);
        return;
      }

      handleResponseInfo(data, response, $this);
      if (data.opts.periodControl) {
        data.opts.periodSelection.setCustomRange(response.isCustomRange ?? false);
        if (response.isCustomRange) {
          data.opts.period = 'custom';
        }
      } else if (data.opts.period === undefined) {
        set(data, 'opts.period', response.period ?? 'tag');
      }
      data.opts.pickerExtended.prepareDateControls(data, response);
      updateTitle(data, response, params);
      applyHeatmapSettings(data);
      applyHeatmapHeight(data);
      excelExportView(response);

      if (response.dataDeviceNames) {
        const dataDeviceSelectionId = 'dataDeviceSelection';
        if (!window.document.getElementById(dataDeviceSelectionId).hasChildNodes()) {
          EventService.dispatch(DataDeviceEvents.MOUNT_DATA_DEVICES_SELECTION, {
            data: {
              mountId: dataDeviceSelectionId,
            },
          });
          EventService.dispatch(DataDeviceEvents.UPDATE_DATA_DEVICE_SELECTION_DEVICES, {
            data: {
              devices: response.dataDeviceNames,
            },
          });
        } else if (data.key !== params.key) {
          EventService.dispatch(DataDeviceEvents.UPDATE_DATA_DEVICE_SELECTION_DEVICES, {
            data: {
              devices: response.dataDeviceNames,
            },
          });
        }
        fn.resetComponentSelection();
        jQuery('#dataDeviceSelection').show();
      }

      data.key = params.key;

      if (callback !== undefined && isFunction(callback)) {
        callback(calledScope);
      }
    }
  };

  fn.requestShouldBePost = function (params) {
    const allowedGetRequestSize = 5300;
    const paramsLength = params.length;
    return paramsLength > allowedGetRequestSize;
  };

  async function update(updateOptions, callback, calledScope, chartScope, errorCallback) {
    if (VCOM.analysis) {
      VCOM.analysis.unhighlightUpdateButton();
    }
    calledScope = calledScope || null;
    let $this = $(this);

    if ($(chartScope).data('chart')) {
      $this = $(chartScope);
    }

    const data = $this.data('chart');

    if (!data) {
      return;
    }

    if (data.updateInProgress) {
      return;
    }

    let params = $.extend({}, data.opts.chart, $this.chart('getChartParams'));
    params.width = data.opts.chart.width;

    if (updateOptions) {
      params = $.extend({}, params, updateOptions);
    }

    if (params.dataSource === 'MD-API') {
      params.body = buildRequestBody(params);
      params.url = AlarmsEndpoint.GET_MEASUREMENT_DATA;
    }

    if (params.key !== undefined) {
      if (params.key === 'Q6ZWP') {
        params.url = GraphEndpoint.GRAPH;
      }
      if (params.key === 'HMSII') {
        params.url = GraphEndpoint.GRAPH;
      }
      if (params.key === 'DSLEN') {
        params.url = GraphEndpoint.GRAPH_WITH_ADDITIONAL_INFO;
      }
      if (params.key === 'CGBCV') {
        params.url = GraphEndpoint.GRAPH_WITH_ADDITIONAL_INFO;
        params.excelExportFromTable = true;
      }
      if (data.opts.minuteValuesToggle) {
        if (data.opts.minuteValuesToggle.excludedChartsByKeys.includes(params.key)) {
          params.displayMinuteValues = false;
        }
      }
    }

    const urlSearchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => urlSearchParams.append(key, `${value}`));

    let clickedMinuteValue = params.displayMinuteValues;

    try {
      data.updateInProgress = true;
      disableUpdateButtons();
      $this.mask();
      callObjectMethod(data.chartObj, 'showLoading');
      EventService.dispatch('showChartLoading');
      if (makeMoment(params.date).isValid() === false) {
        withScope((scope) => {
          scope.setExtra('debug', false);
          captureException(new Error(`Invalid date: ${params.data.date}`));
        });
      }

      const paramsString = urlSearchParams.toString();
      const { url } = params;

      if (params.period === 'custom' && !params.dateTo) {
        withScope((scope) => {
          scope.setExtra('debug', false);
          captureException(new Error('Missing end date in chart for custom period'));
          setContext('invalidCustomConfig', {
            params,
            paramsString,
          });
        });
      }

      if (params.systemId === 0 || params.systemId === '0') {
        captureException(new Error('SystemId cannot be 0'));
        setContext('systemIdZero', {
          params,
          urlSearchParams,
        });
      }
      let response;
      response = await getData(url, params, urlSearchParams, paramsString);

      // Fallback if no minute values are available
      if (params.displayMinuteValues === true && params.displayType !== 'excel') {
        let missingMinuteValues;
        if (response.data.data) {
          missingMinuteValues = filterChartDataWithoutNonNullAndKeepIndex(response.data.data);
        } else {
          missingMinuteValues = filterChartDataWithoutNonNullAndKeepIndex(response.data.highchartsArray.data);
        }
        if (missingMinuteValues.length > 0) {
          EventService.dispatch('missingMinuteValues', { data: missingMinuteValues });
          clickedMinuteValue = false;
          params.displayMinuteValues = false;
          const urlSearchParamsMinuteValues = new URLSearchParams();
          Object.entries(params).forEach(([key, value]) => urlSearchParamsMinuteValues.append(key, `${value}`));
          const paramsStringMinuteValues = urlSearchParamsMinuteValues.toString();

          response = await getData(url, params, urlSearchParamsMinuteValues, paramsStringMinuteValues);
        }
      }

      fn.onUpdateSuccess(response.data, calledScope, callback, data, params, $this);
    } catch (error) {
      if (isFunction(errorCallback)) {
        errorCallback(error);
      } else {
        captureException(error);
        setContext('chartUpdate', {
          params,
          urlSearchParams,
        });
      }
    } finally {
      if (data.opts.minuteValuesToggle) {
        if (clickedMinuteValue) {
          data.opts.minuteValuesToggle.getMinuteValuesToggle(params.systemId, params.chartType);
        } else {
          data.opts.minuteValuesToggle.setActive(false);
          data.opts.minuteValuesToggle.setMinuteValuesToggle(params.systemId, params.chartType, false);
        }
      }
      data.updateInProgress = false;
      enableUpdateChartButton();
      if (params.key !== 'SPF01') {
        enableTodayButton();
      }
      $this.unmask();
      callObjectMethod(data.chartObj, 'hideLoading');
      EventService.dispatch('hideChartLoading');
    }
    if (params.key) {
      logPortletChartClicked(params.key);
    }
    return false;
  }

  function filterChartDataWithoutNonNullAndKeepIndex(chartData) {
    return chartData.map((data) => {
      if (isDataObjectWithDataArray(data)) {
        const hasNonNull = data.data.some((innerArray) => Array.isArray(innerArray) && innerArray[1] !== null);
        return { name: data.name, hasNonNullValues: hasNonNull };
      }
      return { name: data.name, hasNonNullValues: true };
    }).filter((item) => item.hasNonNullValues === false)
      .map(({ name }) => ({ name }));
  }

  async function getData(url, params, urlSearchParams, paramsString) {
    if (params.dataSource === 'MD-API') {
      return HttpService.post(url, params.body);
    }
    if (fn.requestShouldBePost(paramsString) === true) {
      return HttpService.post(url, urlSearchParams);
    }
    return HttpService.get(`${url}?${paramsString}`);
  }

  function isDataObjectWithDataArray(data) {
    return (
      typeof data === 'object'
      && data !== null
      && Object.prototype.hasOwnProperty.call(data, 'data')
      && Array.isArray(data.data)
    );
  }

  function applyHeatmapSettings(chart) {
    if (chart.chartObj?.options?.chart?.type === 'heatmap') {
      chart.chartObj.options.plotOptions.nullColor = '#af272f';
    }
  }

  function applyHeatmapHeight(chart) {
    const chartCanvas = $('#chart-canvas');
    const heatChart = chartCanvas.highcharts();
    if (heatChart === undefined || isChartNotHeatmap(chart)) {
      return;
    }
    const height = $('#chartWidget .ui-widget-content .mc-chart-content').height() - $('.ui-corner-all').height();
    if (chartCanvas.height() >= height || isNaN(height)) {
      return;
    }

    heatChart.setSize($('#chartWidget .ui-widget-content .mc-chart-content').width(), height);
  }

  function excelExportView(response) {
    $('#chartWidget').find('.excelExport').prop('disabled', !response.excelExportView);
  }

  fn.hasChartData = function (chartOptions) {
    const dataSeries = chartOptions.series;
    const polarChart = chartOptions.chart.polar || false;
    if (dataSeries) {
      for (let i = 0; i < dataSeries.length; i += 1) {
        const seriesdata = filter(dataSeries[i].data, (element) => element === 0 || element);
        if (seriesdata) {
          for (let j = 0; j < seriesdata.length; j += 1) {
            if (jQuery.isArray(seriesdata[j]) && polarChart && dataExistsForPolarCharts(seriesdata[j][1])) {
              return true;
            }
            if (jQuery.isArray(seriesdata[j]) && !polarChart && dataExistsForNormalCharts(seriesdata[j][1])) {
              return true;
            }
            if (jQuery.isNumeric(seriesdata[j])) {
              return true;
            }
            if (isPlainObject(seriesdata[j]) && dataExistsForChartsWithYaxisValue(seriesdata[j].y)) {
              return true;
            }
          }
        }
      }
    }
    return false;
  };
  function dataExistsForNormalCharts(dataPoint) {
    return dataPoint !== null && dataPoint !== undefined;
  }

  function dataExistsForPolarCharts(dataPoint) {
    return dataPoint !== null && dataPoint !== undefined && dataPoint !== 0;
  }

  function dataExistsForChartsWithYaxisValue(dataPoint) {
    return dataPoint !== null;
  }

  function logEvaluationDiagramCreatorClicked(chartType) {
    EventLoggingService.logFrontendEvent(
      FrontendEvents.SYSTEM_EVALUATION_DIAGRAM_CREATOR_CLICK(chartType),
    );
  }

  function logPortletChartClicked(chartKey) {
    EventLoggingService.logFrontendEvent(
      FrontendEvents.PORTLET_CHART_LOAD(chartKey),
    );
  }

  function logYearToYearInteraction(response, chartKey, currentSeries) {
    const chartYearToYear = getChartYearToYear(chartKey);
    if (chartYearToYear && response.period === 'jahr' && currentSeries.visible === false) {
      if (currentSeries.visible === false) {
        if (chartKey === 'L6P2F' && currentSeries.unit === '%') {
          response.data.forEach((entry, index) => {
            if (entry.unit === '%') {
              response.data[index].visible = true;
            }
          });
        }
        currentSeries.visible = true;
      }
      if (isItemPreviousYear(currentSeries, response)) {
        const dataSet = chartYearToYear[currentSeries.unit];
        if (!isChartInPortlet()) {
          EventLoggingService.logFrontendEvent(
            FrontendEvents.SYSTEM_EVALUATION_CHART_PREVYEARDATALOAD(chartKey, dataSet),
          );
        } else {
          EventLoggingService.logFrontendEvent(
            FrontendEvents.PORTLET_EVALUATION_CHART_PREVYEARDATALOAD(chartKey, dataSet),
          );
        }
      }
    }
  }

  function getChartYearToYear(chartKey) {
    const availableYearToYearData = {
      LXLXE: {
        kWh: 'energy',
        'Wh/m²': 'irradiation',
      },
      L6P2F: {
        kWh: 'pvYield',
        '%': 'performanceRatio',
      },
      R7105: {
        '%': 'systemAvailability',
      },
    };
    return chartKey in availableYearToYearData ? availableYearToYearData[chartKey] : false;
  }

  function isItemPreviousYear(currentSeries, response) {
    const responseYear = parseInt(response.datetime, 10);
    const pointYear = parseInt((currentSeries.name.split(' ').slice(-1))[0], 10);
    return responseYear !== pointYear;
  }

  function showNoSelectionPlaceholder($this) {
    const placeholder = $this.data('chart').opts.noSelectionPlaceholder;
    if (placeholder) {
      $('.chart-canvas', $this).html(`<div class="no-conf-wrapper">
           <span id="no-conf" class="${placeholder.icon}"></span>
           <p>${placeholder.text.replace(/\n/g, '<br>')}</p>
         </div>`);
    }
  }

  function showError(sLabel, $this, sIcon, solutionLink = []) {
    jQuery('div.chart-error', $this).remove();
    jQuery('.chart-canvas', $this).text('');
    jQuery('.chart-canvas', $this).prepend($('<div class="chart-error"></div>'));
    let html = '<div class="no-conf-wrapper">';
    html += '<span id="no-conf" class="' + sIcon + '"></span>';
    html += '<p>' + translate(sLabel, solutionLink) + '</p>';
    html += '</div>';
    jQuery('div.chart-error', $this).html(html).show();
  }

  function actualizeZusatzinfo(zusatzInfoData) {
    let zusatzinfo = $('.chart-info', $this);
    if (zusatzinfo.length <= 0) {
      return false;
    }

    const $this = $(this);
    let countInfo = 0;

    zusatzinfo.replaceWith($('<div id="zusatzinfo" class="ui-tabs chart-info"><ul><li><a href="#tabs-1"></a></li></ul><div id="tabs-1"><p></p></div></div>'));
    zusatzinfo = $('#zusatzinfo', $this);
    zusatzinfo.tabs();
    const tab = zusatzinfo.find('.ui-tabs-nav li:eq(0)').remove();
    const panelId = tab.attr('aria-controls');
    jQuery('#' + panelId).remove();
    jQuery('tabs').tabs('refresh');

    if (zusatzInfoData) {
      jQuery.each(zusatzInfoData, function (key, value) {
        if (key !== 'information') {
          zusatzinfo.append('<div id="infotab-' + key + '" class="ui-tabs-hide">' + value.content + '</div>');
          jQuery('<li><a href="#infotab-' + key + '">' + value.title + '</a></li>')
            .appendTo('#zusatzinfo .ui-tabs-nav');
          zusatzinfo.tabs('refresh');
        }
        countInfo += 1;
      });
    }
    if (countInfo === 0) {
      zusatzinfo.hide().empty();
    } else {
      zusatzinfo.show();
    }
  }

  function setSize(width, height) {
    const $this = $(this);
    const data = $this.data('chart');
    if (data && data.chartObj) {
      data.chartObj.setSize(width, height, false);
    }
  }

  function showLoading() {
    const $this = $(this);
    const data = $this.data('chart');
    $this.mask();
    if (data && data.chartObj) {
      callObjectMethod(data.chartObj, 'showLoading');
    }
  }

  function getChartParams() {
    const $this = $(this);
    const data = $this.data('chart');
    const chartWidth = $('#chartWidget .mc-chart-content').width();
    const chartHeight = elementScope.id === 'chartWidgetAnalysis'
      ? $('#chartWidgetAnalysis .mc-chart-content').height()
      : $('#chartWidget .mc-chart-content').height();
    let options = {};

    if (chartWidth) {
      options.width = chartWidth - 20;
      options.height = chartHeight * 0.75;
    }

    if ($('#chartSelection').length > 0) {
      options.chartType = data.opts.chart.chartType;
    }

    options = data.opts.pickerExtended.extendParamsByPickersStartAndEndDate(options);

    if (options.key && options.key.indexOf('custom') !== -1) {
      options.url = options.key;
    }

    try {
      const messwertauswahl = $this.chart('getMesswertauswahl');
      if (messwertauswahl.left) {
        options.leftTeilanlagen = messwertauswahl.left.subsystems.join(',');
        options.leftWechselrichter = messwertauswahl.left.inverters.join(',');
        options.leftDataDevices = messwertauswahl.left.datadevices.join(',');
        if (messwertauswahl.left.values.length > 0) {
          options.leftVal = messwertauswahl.left.values.join(',');
        }
      }
      if (messwertauswahl.right) {
        options.rightTeilanlagen = messwertauswahl.right.subsystems.join(',');
        options.rightWechselrichter = messwertauswahl.right.inverters.join(',');
        options.rightDataDevices = messwertauswahl.right.datadevices.join(',');
        if (messwertauswahl.right.values.length > 0) {
          options.rightVal = messwertauswahl.right.values.join(',');
        }
      }
    } catch (Error) {
    }

    options.displayMinuteValues = data.opts.minuteValuesToggle ? data.opts.minuteValuesToggle.getValue() : false;

    if (options.chartType === 'Gak') {
      options.gak = new ComponentSelection('#chartComponentSelection').getSelected();
      if (!options.gak.length) {
        options.gak = null;
      }
    } else if (options.chartType === 'Wechselrichter' || options.chartType === 'mppt') {
      options.inv = new ComponentSelection('#chartComponentSelection').getSelected();
    } else if (options.chartType === 'tracker' || options.chartType === 'meteo' || options.chartType === 'met' || options.chartType === MeasurementType.BAT.key) {
      EventService.addListener(DataDeviceEvents.FETCH_DATA_DEVICES_TO_DISPLAY, { callback: function (event) { options.dataDevicesToDisplay = event.detail.dataDevicesToDisplay; } });
      EventService.dispatch(DataDeviceEvents.TRIGGER_FETCH_OF_DATA_DEVICES_TO_DISPLAY);
    }

    if (data.opts.minuteValuesToggle && data.opts.minuteValuesToggle.isActive() === false) {
      data.opts.minuteValuesToggle.setActive(false);
    }

    options.period = hasPeriodSelection(data.opts)
      ? data.opts.periodSelection.getCurrentPeriod()
      : data.opts.period;

    return $.extend({}, options);
  }

  function getMesswertauswahl() {
    const $this = $(this);

    for (let i = 0; i < 2; i += 1) {
      if ($this.data('axis_' + i) === undefined) {
        fn.multiSelect.initAxisData($this, i);
      }

      $this.data('axis_' + i).values = [];
      $this.data('axis_' + i).subsystems = [];
      $this.data('axis_' + i).inverters = [];
      $this.data('axis_' + i).datadevices = [];

      $('.mc-chart-config-' + i + ' select.chart-main-values').multiselect('widget').find('input:checked').each(function () {
        $this.data('axis_' + i).values = $this.data('axis_' + i).values.concat($(this).val());
      });
      $('select.chart-wr-selection-' + i).multiselect('widget').find('input:checked').each(function () {
        $this.data('axis_' + i).inverters = $this.data('axis_' + i).inverters.concat($(this).val());
      });
      $('select.chart-gak-selection-' + i).multiselect('widget').find('input:checked').each(function () {
        $this.data('axis_' + i).inverters = $this.data('axis_' + i).inverters.concat($(this).val());
      });
      $('select.chart-ddv-selection-' + i).multiselect('widget').find('input:checked').each(function () {
        $this.data('axis_' + i).datadevices = $this.data('axis_' + i).datadevices.concat($(this).val());
      });
    }

    return {
      left: $this.data('axis_0'),
      right: $this.data('axis_1'),
    };
  }

  function setOptions(options) {
    const $this = $(this);
    const data = $this.data('chart');
    $.extend(data.opts.chart, options);
  }

  async function drawComponentSelection(anlage, componentType, chartKey, $this) {
    const selection = new ComponentSelection('#chartComponentSelection');
    selection.onSubmit(function () {
      $this.chart('update');
      if (chartKey !== 'HMSII') {
        selection.setComponentSelection(anlage, selection.getSelected(), componentType);
      }

      selection.hideSelection();
    });

    $this.mask();
    await selection.initSelection(anlage, componentType, chartKey);
    selection.hideSelection();
    await selection.preSelectComponents(anlage, componentType, chartKey);
    $this.unmask();
  }

  function slideUpComponentSelection() {
    new ComponentSelection('#chartComponentSelection').hideSelection();
  }

  fn.resetComponentSelection = function () {
    jQuery('#chartComponentSelection').hide();
  };
  function handleResponseExcel(data, response, scope) {
    callObjectMethod(data.chartObj, 'hideLoading');
    EventService.dispatch('hideChartLoading');
    scope.unmask();
    window.location.href = VCOM.baseUrl + '/default/file/download/get/' + response.tmp_name + '/name/' + response.name;
  }

  fn.switchPeriodOnClick = function (chartOptions, params, scope, datePickerExtended, periodSelection, minuteValuesToggle) {
    chartOptions.series.forEach(function (currentValue) {
      if (fn.isPeriodNotClickable(params.period, scope, chartOptions.chart.type)) {
        return;
      }

      currentValue.cursor = 'pointer';
      currentValue.events.click = function (event) {
        const { period } = params;

        if (period === 'gesamt') {
          params.period = 'jahr';
        }
        if (period === 'jahr') {
          params.period = 'monat';
        }
        if (period === 'monat') {
          params.period = 'tag';
        }

        params.date = getClickedDate(params, chartOptions.chart.type, event.point);

        if (minuteValuesToggle) {
          minuteValuesToggle.toggleVisibilityPeriod(params.period);
        }

        if (isChartInPortlet()) {
          fn.updatePortletChart(params, scope, periodSelection);
        } else {
          fn.updateEvaluationChart(datePickerExtended, params, periodSelection);
        }
      };
    });
  };

  function getClickedDate(params, chartType, point) {
    if (chartType === 'heatmap') {
      return makeMoment(point.x).format();
    }
    if (params.period === 'jahr') {
      return makeMoment(params.date).year(point.category).format();
    }
    if (params.period === 'monat') {
      return makeMoment(params.date).month(point.x).format();
    }
    if (params.period === 'tag') {
      return makeMoment(params.date).date(parseInt(point.category, 10)).format();
    }
  }

  fn.isPeriodNotClickable = function (period, scope, chartType) {
    if (period === 'custom' || period === 'woche' || chartType === 'waterfall') {
      return true;
    }

    return fn.getAvailablePeriods(scope).indexOf(period) < 1;
  };

  fn.getAvailablePeriods = function (scope) {
    let periods;

    if (isChartInPortlet()) {
      [periods] = $(`#ffPeriodSelect_${scope.attr('id')}`);
    } else {
      periods = $('#chart-period-selection input:not(:disabled)');
    }

    return $.map(periods, (val) => $(val).val());
  };

  fn.updateEvaluationChart = function (datePickerExtended, params, periodSelection) {
    periodSelection.removePeriodHighlighting();
    const periodSelector = $(`#chart-period-selection input[value="${params.period}"]`);
    const periodId = periodSelector.attr('id');
    datePickerExtended.modifyStartAndEnd(params.period, params.date);
    $(`#${periodId}`).parent().trigger('click');
  };

  fn.updatePortletChart = function (params, scope, periodSelection) {
    const options = {
      chart: omit(params, 'periodControl'),
      period: params.period,
      displayAnlagenname: true,
      updateFunc: scope.chart('update', params),
    };
    const portletTitle = $(`#portlet_${scope.attr('id')}`).parent().find('span.chartDisplayTitle');
    const portletSubtitle = $(`#portlet_${scope.attr('id')}`).parent().find('span.chartDisplaySubtitle');
    periodSelection.removePeriodHighlighting();
    periodSelection.highlightPeriod(params.period);

    const chartPortletHead = new ChartPortletHead();
    chartPortletHead.appendTo(portletTitle, scope.attr('id'));
    chartPortletHead.appendTo(portletSubtitle, scope.attr('id'));
    assign(options, chartPortletHead.getChartOptions());
    set(options, 'callerScope', this);
    scope.chart(options);
  };

  fn.restoreSeriesVisibility = function (data, chartOptions, params, timerange) {
    if (data.chartObj === undefined || data.chartObj.series === undefined) {
      return;
    }

    if (data.key !== params.key) {
      return;
    }

    if (chartOptions.series === undefined) {
      return;
    }

    if (getChartYearToYear(params.key) && params.period === 'jahr') {
      const viewYear = new Date(params.date).getFullYear();
      const maxYear = timerange ? timerange.maxYear : new Date(data.opts.datepicker.maxDate).getFullYear();
      const minYear = timerange ? timerange.minYear : new Date(data.opts.datepicker.minDate).getFullYear();
      chartOptions.series.forEach((series) => {
        if (viewYear === maxYear) {
          const seriesLabel = series.name.split(' ').slice(0, -1).join(' ');
          const seriesYear = parseInt((series.name.split(' ').slice(-1))[0], 10);
          const seriesLabelRegEx = new RegExp(seriesLabel);
          const occurances = chartOptions.series.filter((value) => seriesLabelRegEx.test(value.name)).length;
          if (seriesYear < viewYear || occurances === 1) {
            const previousSeries = getOffsetSeries(series, data, -1);
            const currentSeries = getOffsetSeries(series, data);
            if (previousSeries !== undefined) {
              series.visible = previousSeries.visible;
            } else if (currentSeries !== undefined) {
              series.visible = currentSeries.visible;
            }
          }
        } else if (viewYear === minYear) {
          const nextSeries = getOffsetSeries(series, data, 1);
          const currentSeries = getOffsetSeries(series, data);
          const seriesYear = parseInt((series.name.split(' ').slice(-1))[0], 10);
          if (seriesYear < viewYear) {
            if (currentSeries !== undefined) {
              series.visible = currentSeries.visible;
            }
          } else if (nextSeries !== undefined) {
            series.visible = nextSeries.visible;
          } else if (currentSeries !== undefined) {
            series.visible = currentSeries.visible;
          }
        } else {
          const nextSeries = getOffsetSeries(series, data, 1);
          const previousSeries = getOffsetSeries(series, data, -1);
          if (previousSeries) {
            series.visible = previousSeries.visible;
          } else if (nextSeries) {
            series.visible = nextSeries.visible;
          }
        }
      });
    } else {
      chartOptions.series.forEach((series) => {
        const previousSeries = data.chartObj.series.find((prevSeries) => prevSeries.name === series.name);

        if (previousSeries === undefined) {
          return;
        }

        series.visible = previousSeries.visible;
      });
    }
  };

  fn.handleResponseDisplayTypeHighcharts = function (data, response, scope, params) {
    let chartOptions = {};
    if (!response.options && !response.highchartsArray.options) {
      return chartOptions;
    }

    if (response.options) {
      chartOptions = response.options;
      chartOptions.series = response.data;
    } else if (response.highchartsArray) {
      chartOptions = response.highchartsArray.options;
      chartOptions.series = response.highchartsArray.data;
    }
    chartOptions = updateYaxisGridline(chartOptions);
    fn.restoreSeriesVisibility(data, chartOptions, params, response?.options?.chart?.timerange);

    const polarChart = chartOptions.chart.polar || false;

    if (chartOptions.series.length <= 10) {
      if (!polarChart) {
        chartOptions.tooltip.shared = true;
      }
      chartOptions.tooltip.borderColor = '#bbbbbb';
    }

    const exportingButtons = {
      contextButton: {
        symbol: null,
        text: '\uec90',
      },
    };

    if (response.helpButtonText) {
      merge(exportingButtons, {
        infoButton: {
          onclick: () => {},
          text: '\ued79',
          _titleKey: 'helpButton',
        },
      });
    }

    if (params.displayAnalysisLink) {
      merge(exportingButtons, {
        analysisButton: {
          onclick: function onclick() {
            redirectToAnalysisSection(params);
          },
          text: '\uebad',
          _titleKey: 'analysisButton',
        },
      });
    }

    if (params.displayEvaluationLink) {
      merge(exportingButtons, {
        evaluationButton: {
          onclick: function onclick() {
            redirectToEvaluationSection(params);
          },
          text: '\uebaa',
          _titleKey: 'evaluationButton',
        },
      });
    }

    merge(chartOptions.exporting, {
      chartOptions: {
        chart: {
          backgroundColor: VCOM.Layout.getTheme() === 'darkify' ? '#272b35' : '#ffffff',
        },
      },
    });

    setPositionOfExportingButtons(exportingButtons, chartOptions);
    merge(chartOptions.exporting.buttons, exportingButtons);

    chartOptions.chart.renderTo = $('.chart-canvas', scope).attr('id');
    chartOptions.tooltip.crosshairs = true;
    chartOptions.tooltip.useHTML = true;
    let height = 0;
    if (chartOptions.chart.type !== undefined && chartOptions.chart.type === 'heatmap') {
      chartOptions.xAxis.dateTimeLabelFormats = {
        day: '%d',
        month: '%b',
      };
      if (response.period === 'monat') {
        chartOptions.xAxis.tickInterval = TimeInMilliseconds.DAY;
      }
      if (response.period === 'jahr') {
        chartOptions.xAxis.tickInterval = TimeInMilliseconds.DAY * 28;
      }

      const rowsCount = chartOptions.yAxis[0].categories.length;
      if (rowsCount > 0) {
        const rowHeight = 3;
        height = rowsCount * rowHeight;
      }
      chartOptions.tooltip.formatter = function () {
        return TooltipService.heatMapFormatter(this, params.key, response, chartOptions.tooltip.valueSuffix);
      };
      chartOptions.colorAxis.labels.formatter = function () {
        return Highcharts.numberFormat(this.value, 2);
      };
    } else if (polarChart) {
      chartOptions.tooltip.valueDecimals = 2;
      chartOptions.tooltip.headerFormat = '<span>{point.key}°</span><br>';
      chartOptions.tooltip.pointFormat = '<tr><td>'
        + translate('evaluation.FREQUENCY')
        + ':&nbsp;</td><td style="text-align: right"><b>{point.y}</b></td></tr>';
      chartOptions.tooltip.valueSuffix = '&nbsp;' + chartOptions.tooltip.valueSuffix;
      chartOptions.xAxis.labels.format = '{value}°';
      chartOptions.xAxis.labels.formatter = function () {
        if (jQuery.inArray(this.value, [0, 90, 180, 270]) !== -1) {
          const alignment = {
            0: translate('evaluation.NORTH-ABBREVIATED'),
            90: translate('evaluation.EAST-ABBREVIATED'),
            180: translate('evaluation.SOUTH-ABBREVIATED'),
            270: translate('evaluation.WEST-ABBREVIATED'),
          };
          return '<span style="font-weight: bold;">' + alignment[this.value] + '</span>';
        }
        return response.categories[this.value / 10] + '°';
      };
    } else if (chartOptions.xAxis.isTimeStamp) {
      chartOptions.tooltip.formatter = function () {
        return TooltipService.dateTimeFormatter(chartOptions.series, this, response, params.key);
      };
    } else if (chartOptions.chart.type === 'waterfall') {
      merge(chartOptions, {
        plotOptions: {
          waterfall: {
            lineColor: VCOM.Layout.getTheme() === 'darkify' ? '#e0e0e3' : '#666666',
          },
        },
      });
      chartOptions.tooltip.formatter = function () {
        return TooltipService.waterfallFormatter(this);
      };
    } else if (params.key === 'C6DI3') {
      chartOptions.tooltip.formatter = function () {
        return TooltipService.inverterPrFormatter(chartOptions, this, params.key);
      };
    } else {
      chartOptions.tooltip.formatter = function () {
        return TooltipService.defaultFormatter(chartOptions, this);
      };
      if (params.key === 'CGBCV') {
        chartOptions.tooltip.formatter = function () {
          return TooltipService.systemEfficiencyFormatter(this, chartOptions);
        };
      }
      if (params.key === 'GBCPL') {
        chartOptions.tooltip.formatter = function () {
          return TooltipService.inputOutputFormatter(this, chartOptions);
        };
      }
    }

    let error = false;
    for (let i = 0; i < chartOptions.series.length; i += 1) {
      const currentSeries = chartOptions.series[i];
      const seriesYAxis = chartOptions.yAxis[currentSeries.yAxis];

      // check for corresponding yAxis
      if (seriesYAxis === undefined) {
        error = true;
        break;
      }
      chartOptions.series[i].events = {
        legendItemClick: function legendItemClick(event) {
          logYearToYearInteraction(response, data.key, currentSeries);
          return highchartLegendItemClick(event, data, this);
        },
      };
      if (chartOptions.chart.type !== 'heatmap') {
        seriesYAxis.labels.formatter = function () {
          const validNumberFormat = /^-?[0-9]+([.,][0-9]+)?$/;
          if (validNumberFormat.test(this.value)) {
            return LocalizationService.formatNumberWithMetricPostfix(this.value);
          }
          return this.value;
        };
      }

      if (currentSeries.isCosPhi) {
        const selectedPeriod = data.opts.periodSelection.getCurrentPeriod();
        if (['tag', 'woche'].includes(selectedPeriod)) {
          currentSeries.data = currentSeries.data.map(([timestamp, value]) => [timestamp, CosPhiService.calculateCosPhi(value)]);
        } else {
          currentSeries.data = currentSeries.data.map((timestamp, value) => [timestamp, CosPhiService.calculateCosPhi(value)]);
        }
        seriesYAxis.labels.formatter = function () {
          return CosPhiService.formatCosPhi(this.value);
        };
        seriesYAxis.labels.useHTML = true;
      }
    }
    if (error) {
      showError('javascript.ERR_DISPLAY_CHART', scope, 'icon-database-remove');
    } else {
      if (data.chartObj !== undefined) {
        $('.chart-canvas', scope).empty();
        callObjectMethod(data.chartObj, 'destroy');
        data.chartObj = undefined;
      }

      if (!fn.hasChartData(chartOptions)) {
        showError('javascript.ERR_GRAPH_NO_DATA', scope, 'icon-database-remove');
        return;
      }

      chartOptions = removeStaticStyleOptions(chartOptions);
      const size = {
        x: params.width || scope.width() - 17,
        y: params.height || scope.height() - 17,
      };
      size.y = Math.max(size.y, height);
      chartOptions.chart.height = size.y;
      chartOptions.chart.width = size.x;
      setExportMenu(chartOptions);
      fn.switchPeriodOnClick(chartOptions, params, scope, data.opts.pickerExtended, data.opts.periodSelection, data.opts.minuteValuesToggle);
      hideChartElementsOnDistractionFreeMode(chartOptions, params);

      data.chartObj = new Highcharts.Chart(chartOptions);
      data.chartObj.hideLoading();
      EventService.dispatch('hideChartLoading');

      if (params.excelExportFromTable) {
        applyExcelExportSettings(data.chartObj, response);
        $('.excelExport').off().on('click', function () {
          data.chartObj.downloadXLS();
        });
      }

      if (has(data.chartObj, 'options.exporting.buttons.infoButton')) {
        const highchartsButton = $(data.chartObj.container).find('.highcharts-button').get(1);
        $(highchartsButton).bootstrapTooltip({ title: response.helpButtonText, trigger: 'hover' });
      }
    }
    if (polarChart) {
      $('.chart-canvas .highcharts-legend-item > span').width(70);
    }
  };

  function hideChartElementsOnDistractionFreeMode(chartOptions, params) {
    if (!Base.isDistractionFreeModeActive()
      || VCOM.section === 'evaluation' || VCOM.section === 'analysis' || !isDistractionFreeModeActiveForChart(params)) {
      return;
    }
    if (chartOptions.xAxis) {
      chartOptions.xAxis.title.text = null;
    }

    if (chartOptions.yAxis) {
      chartOptions.yAxis.forEach((value) => {
        value.title.text = null;
        value.labels.enabled = false;
        value.offset = 0;
      });
    }
    chartOptions.legend.enabled = false;
    chartOptions.exporting.enabled = false;
  }

  function isDistractionFreeModeActiveForChart(params) {
    const distractionFreeModeCharts = ['chartStatic', 'analysisChart', 'parkCharts', 'schemaChart'];
    return params.chart !== undefined && distractionFreeModeCharts.includes(params.chart);
  }

  function applyExcelExportSettings(chart, chartData) {
    chart.options.exporting.filename = generateExcelFilename(chartData);
  }

  function setExportMenu(chartOptions) {
    if (['separator', 'downloadCSV', 'downloadXLS'].every((element) => chartOptions.exporting.buttons.contextButton.menuItems.includes(element))) {
      return;
    }
    chartOptions.exporting.buttons.contextButton.menuItems = chartOptions.exporting.buttons.contextButton.menuItems.concat([
      'separator',
      'downloadCSV',
      'downloadXLS',
    ]);
  }

  function generateExcelFilename(chartData) {
    return chartData.title.replace(/ /g, '_') + '_' + chartData.datetime.replace(/-/g, '_');
  }

  function highchartLegendItemClick(event, data, scope) {
    if (scope.visible) {
      // already visible - check for visibility of other series
      let iCountVisible = 0;
      let iCountHidden = 0;

      Object.values(data.chartObj.series).forEach((series) => {
        if (series.visible) {
          iCountVisible += 1;
        } else {
          iCountHidden += 1;
        }
      });
      // all visible - hide all graphs
      if (iCountVisible === data.chartObj.series.length) {
        invokeMap(data.chartObj.series, 'hide');
        scope.show();
        return false;
      }
      if (iCountHidden === data.chartObj.series.length - 1) {
        invokeMap(data.chartObj.series, 'show');
        return false;
      }
    }
    return true;
  }

  function handleResponseError(data, response, scope) {
    if (data.chartObj !== undefined) {
      $('.chart-canvas', scope).empty();
      callObjectMethod(data.chartObj, 'destroy');
    }
    const icon = response.error.icon ?? 'icon-database-time';
    const solutionLink = response.error.solutionLink ? [response.error.solutionLink] : [];
    const title = data.opts.label ?? null;
    if (data.opts.displayTitle && title !== null) {
      data.opts.displayTitle.text(title);
      data.opts.displayTitle.attr('title', title);
    }
    showError(response.error.message, scope, icon, solutionLink);
    scope.chart('actualizeZusatzinfo', null);
  }

  function handleResponseInfo(data, response, scope) {
    if ($('.chart-info', scope).length > 0) {
      $('.chart-info', scope).empty();
      if (response.info) {
        if (data.opts.chart.key === 'GBCPL') {
          response.table = undefined;
        }
        if (response.table) {
          if ($.isArray(response.info)) {
            response.info = {};
          }
          const dataShowWarning = response.table.includes('icomoon icon-warning2');
          const iconTooltip = translate('evaluation.EXCEEDING_PR_THRESHOLD');
          response.info.data = {
            title: dataShowWarning
              ? `${translate('javascript.TX_DATA')} <span class='icomoon icon-warning2 prThresholdExceeded' title='${iconTooltip}'></span>`
              : translate('javascript.TX_DATA'),
            content: response.table,
          };
        }
        if (response.info.information !== undefined && data.opts.elInfo) {
          setChartInfo(response.title, response.info.information.content, data);
        }
        if (response.options && fn.hasChartData(response.options) && response.zusatzInfo) {
          scope.chart('actualizeZusatzinfo', response.info);
        } else {
          jQuery('div#zusatzinfo').hide();
        }
      }
    }
  }

  function updateTitle(data, response, params) {
    if (data.opts.displayTitle) {
      let { title } = response;
      if (data.opts.displayAnlagenname && response.anlagenname !== undefined) {
        if (data.opts.displayAnlagenname) {
          data.opts.displaySubtitle.text(response.anlagenname);
          data.opts.displaySubtitle.attr('title', response.anlagenname);
        }
      }

      if (Base.isDistractionFreeModeActive() && isDistractionFreeModeActiveForChart(params)) {
        data.opts.displaySubtitle.text('');
        data.opts.displaySubtitle.attr('title', '');
        if (VcomProvider.section === VcomSection.CONTROL_ROOM && response.anlagenname !== undefined) {
          title = response.anlagenname;
        }
      }

      if (params.chart === 'schemaChart') {
        data.opts.displaySubtitle.text(title);
        data.opts.displaySubtitle.attr('title', title);
      } else {
        data.opts.displayTitle.text(title);
        data.opts.displayTitle.attr('title', title);
      }
    }
  }

  function removeStaticStyleOptions(chartOptions) {
    if (chartOptions.chart.type !== 'heatmap') {
      delete chartOptions.yAxis[0].alternateGridColor;
      delete chartOptions.yAxis[0].minorGridLineColor;
      delete chartOptions.xAxis.gridLineWidth;
      chartOptions.xAxis.lineColor = '#bababa';
      chartOptions = updateYaxisGridline(chartOptions);

      for (let i = 1; i < chartOptions.yAxis.length; i += 1) {
        chartOptions.yAxis[i].alternateGridColor = 'transparent';
        chartOptions.yAxis[i].minorGridLineColor = 'transparent';
        chartOptions.yAxis[i].gridLineColor = 'transparent';
      }
    }
    return chartOptions;
  }

  function redirectToAnalysisSection(params) {
    const form = jQuery('<form>', { action: VCOM.baseUrl + '/analysis', method: 'POST' });
    form.append(jQuery('<input>', {
      type: 'hidden', id: 'chartId', name: 'chartId', value: params.chartId,
    }));
    form.append(jQuery('<input>', {
      type: 'hidden', id: 'date', name: 'date', value: params.date,
    }));
    form.append(jQuery('<input>', {
      type: 'hidden', id: 'period', name: 'period', value: params.period,
    }));
    form.appendTo('body').trigger('submit');
  }

  function redirectToEvaluationSection(params) {
    if (!params.systemId) {
      return false;
    }

    const queryParameters = omit(pick(params, ['date', 'key', 'period', 'type', 'sChartKey']), isEmpty);
    if (Object.keys(queryParameters).includes('sChartKey')) {
      queryParameters.key = clone(queryParameters.sChartKey, true);
      delete queryParameters.sChartKey;
    }

    const queryString = new URLSearchParams(queryParameters).toString();

    window.location = VCOM.baseUrl + '/evaluation/index/index/systemId/' + params.systemId + '?' + queryString;
  }

  function setPositionOfExportingButtons(buttons, chartOptions) {
    const showButtonsVertically = (chartOptions.yAxis && chartOptions.yAxis.length > 1);
    let x = showButtonsVertically ? -12 : 0;
    let y = 0;
    forIn(pick(buttons, ['contextButton', 'infoButton', 'evaluationButton', 'analysisButton']), function (button) {
      button.x = button.x ? button.x + x : x;
      button.y = y;
      x = showButtonsVertically ? x : x - 20;
      y = showButtonsVertically ? y + 20 : y;
    });
  }

  function getAvailablePeriodsForChart(chartKey, chartListGrouped) {
    return Object.values(chartListGrouped).flatMap((chart) => chart.charts)
      .find((chart) => chart.key === chartKey)?.periods ?? [];
  }

  function setChartInfo(title, content, dataSource) {
    const tooltip = translate('common.INFORMATION');
    dataSource.opts.elInfo.html(`<h3><span class='icon-info2' title='${tooltip}'></span> ${title}</h3>${content}`);
  }

  function bindPrevNextControlsListeners(scope, options) {
    $(options.prevNextControls[0]).on('click', function () {
      const period = hasPeriodSelection(options)
        ? options.periodSelection.getCurrentPeriod()
        : options.period;

      options.pickerExtended.modifyStartAndEndByPrevControl(period);
      triggerChartUpdate(options.updateFunc ?? null, scope, { period });
    });

    $(options.prevNextControls[1]).on('click', function () {
      const period = hasPeriodSelection(options)
        ? options.periodSelection.getCurrentPeriod()
        : options.period;

      options.pickerExtended.modifyStartAndEndByNextControl(period);
      triggerChartUpdate(options.updateFunc ?? null, scope, { period });
    });
  }

  function unbindPrevNextControlsListeners(prevNextControls) {
    $(prevNextControls[0]).off('click');
    $(prevNextControls[1]).off('click');
  }

  function hasPeriodSelection(options) {
    return options.periodSelection
    && options.periodSelection.getCurrentPeriod() !== null
    && options.periodSelection.getCurrentPeriod() !== undefined;
  }

  function disableUpdateButtons() {
    jQuery('.updateChart, .todayButton').prop('disabled', true);
  }

  function enableUpdateChartButton() {
    jQuery('.updateChart').prop('disabled', false);
  }

  function enableTodayButton() {
    jQuery('.todayButton').prop('disabled', false);
  }

  function enablePrevNextControls() {
    const $this = $(this);
    const data = $this.data('chart');
    if (data.opts.prevNextControls) {
      $(data.opts.prevNextControls[0]).prop('disabled', false);
      $(data.opts.prevNextControls[1]).prop('disabled', false);
      bindPrevNextControlsListeners($this, data.opts);
    }
  }

  function disablePrevNextControls() {
    const $this = $(this);
    const data = $this.data('chart');
    if (data.opts.prevNextControls) {
      $(data.opts.prevNextControls[0]).prop('disabled', true);
      $(data.opts.prevNextControls[1]).prop('disabled', true);
      unbindPrevNextControlsListeners(data.opts.prevNextControls);
    }
  }

  function transformMdApiResponse(seriesArray, response) {
    return seriesArray.map((series) => {
      const { name } = series;
      const data = Object.entries(response.data).map(([timestamp]) => [makeMoment.utc(timestamp.substring(0, 19)).toDate().getTime(), response.data[timestamp][name]]);
      return {
        name: series.name,
        unit: series.unit,
        visible: true,
        yAxis: series.axis,
        tooltip: {
          decimals: 2,
        },
        data,
      };
    });
  }

  function handleResponseMdApi(response, params, $this) {
    const data = $this.data('chart');
    const requestedDate = params.date;
    if (requestedDate) {
      const localeFormat = makeMoment.localeData(VcomProvider.LanguageSwitch.getCurrentLocale()).longDateFormat('L');
      data.opts.pickerExtended.prepareDateControls(
        data,
        {
          datetime: makeMoment(requestedDate).set({ hour: 0, minute: 0, second: 0 }).format('YYYY-MM-DD HH:mm:ss'),
          datetimeEnd: makeMoment(requestedDate).set({ hour: 23, minute: 59, second: 59 }).format('YYYY-MM-DD HH:mm:ss'),
          datetimeLoc: makeMoment(requestedDate).format(localeFormat),
          datetimeLocEnd: makeMoment(requestedDate).format(localeFormat),
          period: 'tag',
        },
      );
    }

    let seriesData = transformMdApiResponse(data.opts.chart.series, response);
    if (data.opts.chart.seriesTransformer) {
      seriesData = data.opts.chart.seriesTransformer(seriesData, data.opts.chart);
    }

    const { title, systemName } = params;
    if (data.opts.displayAnlagenname) {
      data.opts.displaySubtitle.text(systemName);
      data.opts.displaySubtitle.attr('title', systemName);
    }

    data.opts.displayTitle.text(title);
    data.opts.displayTitle.attr('title', title);

    $this.data('chart', $.extend(data, params));
    fn.handleResponseDisplayTypeHighcharts(
      data,
      {
        options: $.extend(data.options, { series: seriesData }),
        data: seriesData,
      },
      $this,
      params,
    );
  }

  function buildRequestBody(params) {
    const body = {
      systemId: params.systemId,
      payload: {
        from: makeMoment.utc(params.date).tz(params.timeZone).set({ hour: 0, minute: 0, second: 0 }),
        to: makeMoment.utc(params.date).tz(params.timeZone).set({ hour: 23, minute: 59, second: 59 }),
        data: {},
      },
    };
    params.series.forEach((series) => {
      body.payload.data[series.name] = {
        type: series.deviceType,
        id: series.id,
        abbreviation: series.abbreviation,
      };
    });
    return body;
  }
  function updateYaxisGridline(chartOptions) {
    if (!chartOptions.yAxis[0]) {
      return chartOptions;
    }
    if (VCOM.Layout.getTheme() === 'darkify') {
      chartOptions.yAxis[0].gridLineColor = '#777777';
    } else {
      chartOptions.yAxis[0].gridLineColor = '#E9E9E9';
    }
    return chartOptions;
  }

  function getOffsetSeries(series, data, offset = 0) {
    const offsetSeriesYear = parseInt((series.name.split(' ').slice(-1))[0], 10) + offset;
    const offsetSeriesLabel = series.name.split(' ').slice(0, -1).join(' ') + ' ' + offsetSeriesYear;
    return data.chartObj.series.find((offsetSeries) => offsetSeries.name === offsetSeriesLabel);
  }

  async function getSystemInfo(systemId) {
    return $.ajax({
      type: 'POST',
      url: `${VCOM.baseUrl}/default/index/get-anlage-info`,
      data: {
        systemId,
      },
    });
  }

  const publicFunctions = {
    actualizeZusatzinfo,
    enablePrevNextControls,
    disablePrevNextControls,
    getChartParams,
    getMesswertauswahl,
    hideChartElementsOnDistractionFreeMode,
    initMesswertauswahl,
    setOptions,
    setSize,
    showLoading,
    triggerChartUpdate,
    update,
    wasLatestDateSelected,
    getSystemInfo,
  };

  $.fn.chart = function (method, ...args) {
    if (publicFunctions[method]) {
      return publicFunctions[method].apply(this, args);
    }
    if (typeof method === 'object' || !method) {
      return init.apply(this, [method, ...args]);
    }
    $.error('Method ' + method + ' does not exist on jQuery.chart');
  };
}(jQuery));
